import React, { useContext, useMemo, useState } from 'react';

type WithId = {
  id: string;
};

export type DataContextData<T extends WithId> = {
  data: T[];
  removeOne: (id: string) => void;
  addOne: (object: T) => void;
  setData: (data: T[]) => void;
  updateOne: (data: T) => void;
  addOneAtStart: (data: T) => void;
};

export function createDataContext<T extends WithId>() {
  const DataContext = React.createContext<DataContextData<T>>(
    {} as DataContextData<T>
  );

  const DataContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [data, setData] = useState<T[]>([]);

    const updateOne = (object: T) => {
      setData((prev) =>
        prev.map((item) => (item.id === object.id ? object : item))
      );
    };

    const removeOne = (id: string) => {
      setData((prev) => prev.filter((item) => item.id !== id));
    };

    const addOne = (object: T) => {
      setData((prev) => [...prev, object]);
    };

    const addOneAtStart = (object: T) => {
      setData((prev) => [object, ...prev]);
    };

    const contextValue = useMemo(
      () => ({
        data,
        setData,
        removeOne,
        addOne,
        updateOne,
        addOneAtStart,
      }),
      [data]
    );

    return (
      <DataContext.Provider value={contextValue}>
        {children}
      </DataContext.Provider>
    );
  };

  const useDataContext = () => useContext(DataContext);

  return {
    DataContextProvider,
    useDataContext,
  };
}
